import React, { useMemo } from "react";

import type { UserID } from "../../../../../hooks/useAccount";
import type { ProjectMember } from "../../../../../hooks/useProjectSharing";
import {
  type ProjectViewLinkMember,
  useMutateUpdateProjectViewLinkUser,
} from "../../../../../hooks/useProjectViewLink";
import { useTracker } from "../../../../../hooks/useTracker";
import {
  useWebExport,
  WebExportAssignableUserRoles,
  WebExportAssignableUserRolesDisplayName,
} from "../../../../../hooks/useWebExport";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface MemberRowProps {
  member: ProjectViewLinkMember;
  projectUser?: ProjectMember;
  onRemove?: (userId: UserID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  isCurrentUser?: boolean;
  tooltipText?: string;
}

export const MemberRowViewLink = React.forwardRef<ShareRowRef, MemberRowProps>(
  (
    { member, onRemove, containerIntersectionObserver, isCurrentUser, tooltipText, projectUser },
    ref
  ) => {
    const tracker = useTracker();
    const { data: webExportData, viewLinkId } = useWebExport();

    const {
      mutate: mutateProjectViewLinkUser,
      isPending: isMutateUserPending,
      isError: isUpdateError,
    } = useMutateUpdateProjectViewLinkUser({
      userId: member.userId,
    });

    const availableRoles = useMemo(() => {
      if (isCurrentUser) {
        return [member.role] as const;
      }
      return WebExportAssignableUserRoles;
    }, [isCurrentUser, member.role]);

    return (
      <ShareRow
        ref={ref}
        roles={availableRoles}
        roleDisplayName={WebExportAssignableUserRolesDisplayName}
        entity={member}
        containerIntersectionObserver={containerIntersectionObserver}
        onUpdate={(prevRole, newRole) => {
          tracker.trackShareModalMemberRoleChanged({
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            affectedUserId: member.userId,
            oldValue: prevRole,
            newValue: newRole,
            sharedResource: "review_link",
          });
          mutateProjectViewLinkUser({
            role: newRole,
          });
        }}
        onRemove={() => {
          onRemove?.(member.userId);
          tracker.trackShareModalMemberRemoveIntended({
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            affectedUserId: member.userId,
            sharedResource: "review_link",
          });
        }}
        sharingBadges={[
          ...(isCurrentUser ? ["You" as const] : []),
          ...(projectUser?.isTeamUser === false ? ["Guest" as const] : []),
        ]}
        isPending={isMutateUserPending}
        isError={isUpdateError}
        removeActionText={isCurrentUser ? "Leave..." : "Remove..."}
        tooltipText={tooltipText}
      />
    );
  }
);
